import { BaseApiService } from "@mirage/services/BaseApiService";
import { ICommentService } from "@shared/publicportal/services/ICommentService";
import { CreateCommentRequest, GetPublicCommentsResponse, GetCommentsRequest, GetUserCommentsResponse, CreateCommentResponse } from "@shared/publicportal/contracts/Comments";
import { FileChunk } from "@shared/mirage/core/contracts/UploadedFile";
import { StringsHelper } from "@shared/mirage/helpers/StringsHelper";
import { MirageConstants } from "@shared/mirage/mirageConstants";

export class CommentsService extends BaseApiService implements ICommentService {
    constructor() {
        super("publicportal/comments");
    }

    async create(request: Omit<CreateCommentRequest, "user" | "uuid">): Promise<CreateCommentResponse> {
        const uuid = StringsHelper.generateUUID();
        const chunkSize = MirageConstants.MaxChunkSize;
        const fileMap = new Map<string, number>();
        const documentsUploaded = await this.uploadAttachments(fileMap, request, chunkSize, uuid);
        if (!documentsUploaded) {
            return { errorMsg: "An error occurred while submitting the form" };
        }
        request.uuId = uuid;
        const response = await this.httpInstance().post("/", request);
        return response.data;
    }

    async getPublicComments(paId: number): Promise<GetPublicCommentsResponse> {
        const response = await this.httpInstance().get(`/${paId}`);
        return response.data;
    }
    async getUserComments(request: Omit<GetCommentsRequest, "publicUserId">): Promise<GetUserCommentsResponse> {
        const response = await this.httpInstance().get(`/userComments/${request.paId}`);
        return response.data;
    }

    private async uploadAttachments(fileMap: Map<string, number>, req: Omit<CreateCommentRequest, "user" | "uuid">, chunkSize: number, uuid: string) {
        const files = req.attachments;
        const requests = [];
        for (const file of files) {
            if (!fileMap.has(file.name)) {
                fileMap.set(file.name, 1);
            } else {
                const cnt = fileMap.get(file.name) as number;
                fileMap.set(file.name, cnt + 1);
            }
            const fileName = `${fileMap.get(file.name)}.${file.name}`;
            const totalChunks = Math.ceil(file.size / chunkSize);
            let startByte = 0;
            for (let i = 1; i <= totalChunks; i++) {
                const endByte = Math.min(startByte + chunkSize, file.size);
                const chunk = file.slice(startByte, endByte, "application/octet-stream");
                const promise = this.uploadChunk(fileName, chunk, i, totalChunks, uuid);
                startByte = endByte;
                requests.push(promise);
            }
        }
        const responses = await Promise.all(requests);
        req.attachments = files.map((file) => {
            const fileName = `${fileMap.get(file.name)}.${file.name}`;
            return { name: fileName } as File;
        });
        return responses.every((r) => r);
    }

    private async uploadChunk(fileName: string, chunk: Blob, chunkNumber: number, totalChunks: number, uuid: string) {
        try {
            const payload: FileChunk = {
                fileName,
                chunk: Object.values(new Uint8Array(await chunk.arrayBuffer())),
                uuid,
                chunkNumber,
                totalChunks,
            };

            const response = await this.httpInstance().post("/uploadChunk/", payload);
            return response.status === 200;
        } catch (error) {
            console.error("Error uploading chunk", error);
            return false;
        }
    }
}
